/*----------------------------------
- HOOK: HOME DOMAINS PREVIEW HANDOFF
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @fix  docs/fixes/2026-09-21-signup-wall-repository-headline.md
 * @rule No persona on any click from the preview: the account screen asks. Every Home preview
 *       event carries the same page and section ids, exported below, so the drawer's events and
 *       the handoff's can be joined.
 *
 * The Home preview's three handoffs, See all, the row "+" and the drawer's CTA, mirroring `navigateToProduct` on
 * `/domains` (`DomainsLandingPage.tsx`) with Home's own page, section and trigger ids. The drawer
 * closes itself before its CTA calls `handleUnlockRow`, so nothing here has to clear it.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import useContext from '@/client/context';
import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import { WATCHLIST_ADD_ACTION } from '@uniquedomains/components/contracts/upgrade/catalogs/featureActions';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { buildProductDomainUrl } from '@/client/pages/landing/domains/page/lib/publicRadarQuery';
import { buildSearchConversionContext } from '@/client/services/conversion/intentContext';
import { buildWebsiteAuthHref, openWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { toAbsoluteProductUrlWithOrigin } from '@/client/utils/payment/productUrls';
import { encodeRadarFiltersUrl } from '@/common/libs/filters';

// Types
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import type { WebsiteConversionContext } from '@uniquedomains/components/conversion/authIntent';
import type { WebsiteConversionWallAction } from '@uniquedomains/components/conversion/wallActions';
import type * as Desired from '@/server/services/Conversion/Desired';
import type { WebsiteDomainsLandingResponse } from '@/common/types/domains/websiteLanding';

/*----------------------------------
- TYPES
----------------------------------*/

type HomePreviewIntent = {
    trackingId: 'see_all' | 'open_row' | 'add_row';
    desiredFeature: NonNullable<Desired.TDesired['feature']>;
    desiredAction: WebsiteConversionWallAction;
    desiredPage: string;
    context: WebsiteConversionContext | undefined;
};

type HomeDomainsPreviewHandoffInput = {
    search: WebsiteDomainsLandingResponse;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The page id every other Home emitter uses (`LandingExperience`, the Header's `trackingPage`). */
export const HOME_DOMAINS_PREVIEW_PAGE = 'landing_home';
/** The section the trigger catalog names for the Home preview (`website_landing_*` surfaces). */
export const HOME_DOMAINS_PREVIEW_SECTION = 'landing_domains_preview';

const TRIGGER_BY_INTENT: Record<HomePreviewIntent['trackingId'], ConversionTriggerId> = {
    see_all: 'website_landing_see_all',
    open_row: 'website_landing_open_row',
    add_row: 'website_landing_add_row',
};

/*----------------------------------
- HOOK
----------------------------------*/

const useHomeDomainsPreviewHandoff = ({ search }: HomeDomainsPreviewHandoffInput) => {
    const { publicOrigins } = useContext();
    const productHref = React.useMemo(
        () => toAbsoluteProductUrlWithOrigin(
            encodeRadarFiltersUrl(search.effectiveFilters ?? {}, { surface: 'product' }),
            publicOrigins.productOrigin,
        ),
        [publicOrigins.productOrigin, search.effectiveFilters],
    );

    const navigateToProduct = React.useCallback(
        (intent: HomePreviewIntent) => {
            if (typeof window === 'undefined') return;

            const authHref = buildWebsiteAuthHref({
                flow: 'cold',
                mode: 'signup',
                returnTo: intent.desiredPage,
                context: intent.context,
                feature: intent.desiredFeature,
                action: intent.desiredAction,
                from: `website_landing_${intent.trackingId}`,
            });
            trackLandingCta({
                page: HOME_DOMAINS_PREVIEW_PAGE,
                section: HOME_DOMAINS_PREVIEW_SECTION,
                label: intent.trackingId,
                kind: 'product',
                href: authHref,
                feature: intent.desiredFeature,
                action: intent.desiredAction,
                surface: 'results',
            });
            trackConversionTrigger(TRIGGER_BY_INTENT[intent.trackingId], {
                action: intent.desiredAction,
                feature: intent.desiredFeature,
                href: authHref,
                page: intent.desiredPage,
                section: HOME_DOMAINS_PREVIEW_SECTION,
            });
            track('cta_open_screener_click', {
                page: HOME_DOMAINS_PREVIEW_PAGE,
                intent: intent.trackingId,
                feature: intent.desiredFeature,
                action: intent.desiredAction,
            });

            openWebsiteAuthHref(authHref);
        },
        [],
    );

    /** The whole search: the search headline, and the product list as the destination. */
    const handleSeeAll = React.useCallback(() => {
        navigateToProduct({
            trackingId: 'see_all',
            desiredFeature: 'databaseAccess',
            desiredAction: DATABASE_SEARCH_ACTIONS.seeAll,
            desiredPage: productHref,
            context: buildSearchConversionContext({
                search,
                filterChips: [],
                heroTitle: search.humanized,
                cameFromGithub: false,
            }),
        });
    }, [navigateToProduct, productHref, search]);

    /**
     * The drawer's CTA names the row, as on `/domains`: the account screen reads "See {domain}:
     * price, history and risk", and `returnTo` opens that row's panel over the product search.
     */
    const handleUnlockRow = React.useCallback(
        (domain: string) => {
            navigateToProduct({
                trackingId: 'open_row',
                desiredFeature: 'databaseAccess',
                desiredAction: DATABASE_SEARCH_ACTIONS.unlockDomainDetails,
                desiredPage: buildProductDomainUrl(productHref, domain),
                context: buildSearchConversionContext({
                    search,
                    filterChips: [],
                    heroTitle: search.humanized,
                    cameFromGithub: false,
                    domain,
                }),
            });
        },
        [navigateToProduct, productHref, search],
    );

    /** The row "+": the Watchlist gate, landing on the same row URL as the drawer's CTA. */
    const handleAddRow = React.useCallback(
        (domain: string) => {
            navigateToProduct({
                trackingId: 'add_row',
                desiredFeature: 'watchlistCount',
                desiredAction: WATCHLIST_ADD_ACTION,
                desiredPage: buildProductDomainUrl(productHref, domain),
                context: buildSearchConversionContext({
                    search,
                    filterChips: [],
                    heroTitle: search.humanized,
                    cameFromGithub: false,
                    domain,
                }),
            });
        },
        [navigateToProduct, productHref, search],
    );

    return { handleSeeAll, handleUnlockRow, handleAddRow };
};

export default useHomeDomainsPreviewHandoff;
